declare global {
  interface Window {
    lazyLoad: any;
  }
}

/**
 * Attribute used to make an accordion.
 *
 * Usage:
 * ------
 * ```html
    <div
        data-kb-accordion
        data-kb-accordion-is-exclusive <!-- if present, opening an item will close all the others -->
        data-kb-accordion-items=".item"
        data-kb-accordion-triggers=".trigger">
        <div class="item is-active">
            <div class="trigger">Toggle first block!</div>

            <div class="body">Content of the first block</div>
        </div>

        <div class="item">
            <div class="trigger">Toggle second block!</div>

            <div class="body">Content of the second block</div>
        </div>

        <div class="item">
            <div class="trigger">Toggle third block!</div>

            <div class="body">Content of the third block</div>
        </div>
    </div>
   ```
 *
 * @export
 * @class AccordionManager
 */
export class AccordionManager {
  /**
   * The selector used to find all accordion elements, should work with
   * `document.querySelectorAll()`
   *
   * @private
   * @type {string}
   * @memberof AccordionManager
   */
  private selector: string = "[data-kb-accordion]";

  /**
   * The name of the data attribute containing the accordion items selector, in
   * camelCase, without the `data-` part.
   *
   * @private
   * @memberof AccordionManager
   */
  private accordionItemsSelectorAttribute = "kbAccordionItems";

  /**
   * The name of the data attribute containing the accordion triggers selector,
   * in camelCase, without the `data-` part.
   *
   * @private
   * @memberof AccordionManager
   */
  private accordionTriggersSelectorAttribute = "kbAccordionTriggers";

  /**
   * The name of the data attribute containing the boolean to make the
   * accordion exclusive, in camelCase, without the `data-` part.
   *
   * @private
   * @memberof AccordionManager
   */
  private accordionIsExclusiveAttribute = "kbAccordionIsExclusive";

  /**
   * Create a new AccordionManager instance.
   * @memberof AccordionManager
   */
  constructor() {
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        () => {
          this.onContentLoaded();
        },
        false
      );
    } else {
      this.onContentLoaded();
    }
  }

  /**
   * Register the accordion manager on every targeted elements.
   *
   * @private
   * @memberof AccordionManager
   */
  private onContentLoaded(): void {
    const elements = document.querySelectorAll<HTMLElement>(this.selector);

    Array.prototype.forEach.call(elements, (el: HTMLElement) => {
      const accordionItemsSelector: string | undefined =
        el.dataset[this.accordionItemsSelectorAttribute];
      const accordionTriggersSelector: string | undefined =
        el.dataset[this.accordionTriggersSelectorAttribute];
      const accordionIsExclusive: boolean = this.accordionIsExclusiveAttribute in el.dataset;

      if (!accordionItemsSelector || !accordionTriggersSelector) {
        return;
      }

      const accordionItems: NodeListOf<HTMLElement> = el.querySelectorAll<
        HTMLElement
      >(accordionItemsSelector);

      if (accordionItems.length === 0) {
        return;
      }

      Array.prototype.forEach.call(accordionItems, (item: HTMLElement) => {
        const trigger: HTMLElement | null = item.querySelector<HTMLElement>(
          accordionTriggersSelector
        );

        if (!trigger) {
          return;
        }

        trigger.addEventListener('click', _ => {
          if (item.classList.contains('is-active')) {
            item.classList.remove('is-active');
            return;
          }

          if (accordionIsExclusive) {
            Array.prototype.forEach.call(accordionItems, (accordionItem: HTMLElement) => {
              accordionItem.classList.remove('is-active');
            });
          }

          item.classList.add('is-active');
          window.lazyLoad.update();
        })
      });
    });
  }
}
